import "./AiSuggestion.scss"

import React, { useEffect, useRef, useState } from "react"
import { Button } from "@fider/components/common/Button"
import { Icon } from "@fider/components/common/Icon"
import { useFider } from "@fider/hooks"
import { actions } from "@fider/services"
import { ContentSuggestion, SuggestionKind } from "@fider/services/actions/ai"
import IconSparkles from "@fider/assets/images/heroicons-sparkles-outline.svg"
import { i18n } from "@lingui/core"
import { Trans } from "@lingui/react/macro"

interface Snapshot {
  title?: string
  text: string
}

interface AiSuggestionProps {
  kind: SuggestionKind
  // title là undefined với bình luận (không có tiêu đề)
  title?: string
  text: string
  disabled?: boolean
  onApply: (title: string | undefined, text: string) => void
}

const DEBOUNCE_MS = 1500
const MIN_CHARS = 12

const signatureOf = (title: string | undefined, text: string) => `${title ?? ""}\n${text}`

// Tùy biến KHCN: gợi ý chuẩn hóa văn bản bằng AI, chỉ hiển thị bên dưới ô nhập.
// Người dùng bấm "Áp dụng" mới thay nội dung, và có thể Hoàn tác / Làm lại.
export const AiSuggestion = (props: AiSuggestionProps) => {
  const fider = useFider()
  const enabled = fider.settings.aiSuggestionEnabled && fider.session.isAuthenticated && !props.disabled

  const [suggestion, setSuggestion] = useState<ContentSuggestion | undefined>(undefined)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | undefined>(undefined)
  const [undoStack, setUndoStack] = useState<Snapshot[]>([])
  const [redoStack, setRedoStack] = useState<Snapshot[]>([])
  const timer = useRef<number>()
  const lastRequested = useRef("")
  const latest = useRef<Snapshot>({ title: props.title, text: props.text })

  const signature = signatureOf(props.title, props.text)
  latest.current = { title: props.title, text: props.text }

  useEffect(() => {
    if (!enabled) {
      return
    }
    window.clearTimeout(timer.current)
    setError(undefined)

    if (props.text.trim().length < MIN_CHARS) {
      setSuggestion(undefined)
      return
    }
    if (signature === lastRequested.current) {
      return
    }

    timer.current = window.setTimeout(async () => {
      lastRequested.current = signature
      setLoading(true)
      const result = await actions.suggestContent(props.kind, props.title, props.text)
      setLoading(false)

      // Người dùng đã gõ tiếp trong lúc chờ: bỏ kết quả cũ
      if (signatureOf(latest.current.title, latest.current.text) !== signature) {
        return
      }

      if (result.ok) {
        const differs = result.data.text !== props.text || (props.title !== undefined && result.data.title !== props.title)
        setSuggestion(result.data.changed && differs ? result.data : undefined)
      } else {
        setSuggestion(undefined)
        setError(result.error?.errors?.[0]?.message)
      }
    }, DEBOUNCE_MS)

    return () => window.clearTimeout(timer.current)
  }, [signature, enabled])

  if (!enabled) {
    return null
  }

  const applySnapshot = (snapshot: Snapshot) => {
    lastRequested.current = signatureOf(snapshot.title, snapshot.text)
    setSuggestion(undefined)
    props.onApply(snapshot.title, snapshot.text)
  }

  const apply = () => {
    if (!suggestion) {
      return
    }
    setUndoStack((stack) => [...stack, { title: props.title, text: props.text }])
    setRedoStack([])
    const nextTitle = props.title !== undefined ? suggestion.title || props.title : undefined
    applySnapshot({ title: nextTitle, text: suggestion.text })
  }

  const dismiss = () => {
    setSuggestion(undefined)
  }

  const undo = () => {
    const previous = undoStack[undoStack.length - 1]
    if (!previous) {
      return
    }
    setUndoStack((stack) => stack.slice(0, -1))
    setRedoStack((stack) => [...stack, { title: props.title, text: props.text }])
    applySnapshot(previous)
  }

  const redo = () => {
    const next = redoStack[redoStack.length - 1]
    if (!next) {
      return
    }
    setRedoStack((stack) => stack.slice(0, -1))
    setUndoStack((stack) => [...stack, { title: props.title, text: props.text }])
    applySnapshot(next)
  }

  const showTitle = suggestion && props.title !== undefined && suggestion.title && suggestion.title !== props.title
  const showHistory = undoStack.length > 0 || redoStack.length > 0

  if (!suggestion && !loading && !error && !showHistory) {
    return null
  }

  return (
    <div className="c-ai-suggestion" aria-live="polite">
      {loading && !suggestion && (
        <div className="c-ai-suggestion__status">
          <Icon sprite={IconSparkles} className="c-ai-suggestion__icon" />
          <Trans id="khcn.ai.suggestion.loading">Checking spelling and grammar...</Trans>
        </div>
      )}
      {error && !suggestion && <div className="c-ai-suggestion__status c-ai-suggestion__status--error">{error}</div>}
      {suggestion && (
        <div className="c-ai-suggestion__card">
          <div className="c-ai-suggestion__header">
            <Icon sprite={IconSparkles} className="c-ai-suggestion__icon" />
            <span className="c-ai-suggestion__title">
              <Trans id="khcn.ai.suggestion.title">Suggested edit</Trans>
            </span>
            {suggestion.note && <span className="c-ai-suggestion__note">{suggestion.note}</span>}
          </div>
          {showTitle && (
            <div className="c-ai-suggestion__field">
              <span className="c-ai-suggestion__label">{i18n._({ id: "khcn.ai.suggestion.titlelabel", message: "Title" })}</span>
              <div className="c-ai-suggestion__text">{suggestion.title}</div>
            </div>
          )}
          {suggestion.text !== props.text && (
            <div className="c-ai-suggestion__field">
              <span className="c-ai-suggestion__label">{i18n._({ id: "khcn.ai.suggestion.textlabel", message: "Content" })}</span>
              <div className="c-ai-suggestion__text">{suggestion.text}</div>
            </div>
          )}
          <div className="c-ai-suggestion__actions">
            <Button variant="primary" size="small" onClick={apply}>
              <Trans id="khcn.ai.action.apply">Apply</Trans>
            </Button>
            <Button variant="tertiary" size="small" onClick={dismiss}>
              <Trans id="khcn.ai.action.dismiss">Dismiss</Trans>
            </Button>
            <span className="c-ai-suggestion__hint">
              <Trans id="khcn.ai.suggestion.hint">Generated by AI. Review before applying.</Trans>
            </span>
          </div>
        </div>
      )}
      {showHistory && (
        <div className="c-ai-suggestion__history">
          <Button variant="tertiary" size="small" onClick={undo} disabled={undoStack.length === 0}>
            <Trans id="khcn.ai.action.undo">Undo</Trans>
          </Button>
          <Button variant="tertiary" size="small" onClick={redo} disabled={redoStack.length === 0}>
            <Trans id="khcn.ai.action.redo">Redo</Trans>
          </Button>
        </div>
      )}
    </div>
  )
}
